import React, { useMemo, useState, useRef } from "react";
import dayjs from "dayjs";
import { useTheme } from "@mui/material/styles";
import { Grid, Typography, IconButton, Menu, MenuItem, Box } from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";

import SkeletonTotalGrowthBarChart from "./ui-component/cards/Skeleton/TotalGrowthBarChart";

interface TotalGrowthPieChartProps {
  isLoading: boolean;
  dataChartCustomersList: number[];
  dataCharDriverList: number[];
  dataChartVehiclesList: number[];
}

const typographyPrometo = { fontFamily: "Prometo, sans-serif" };

const CustomTooltip = ({ active, payload, theme }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    return (
      <div
        style={{
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          padding: '12px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: `1px solid ${theme.palette.divider}`,
          ...typographyPrometo
        }}
      >
        <Typography variant="body2" style={{ ...typographyPrometo, color: data.payload.fill, fontWeight: "bold" }}>
          {data.name}: {data.value}
        </Typography>
      </div>
    );
  }
  return null;
};

const TotalGrowthPieChart = ({
  isLoading,
  dataChartCustomersList,
  dataCharDriverList,
  dataChartVehiclesList
}: TotalGrowthPieChartProps) => {
  const theme = useTheme();
  const chartRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Aesthetic System Theme (Imagem Referência)
  const colorClientes = "#b0bec5"; // Prata/Cinza
  const colorVeiculos = "#00bcd4"; // Ciano
  const colorMotoristas = "#5e35b1"; // Roxo

  const [hiddenSeries, setHiddenSeries] = useState<string[]>([]);

  const formattedData = useMemo(() => {
    const sumCustomers = dataChartCustomersList?.[dayjs().month()] || 0;
    const sumVehicles = dataChartVehiclesList?.[dayjs().month()] || 0;
    const sumDrivers = dataCharDriverList?.[dayjs().month()] || 0;

    return [
      { name: "Clientes", value: sumCustomers, fill: colorClientes },
      { name: "Veículos", value: sumVehicles, fill: colorVeiculos },
      { name: "Motoristas", value: sumDrivers, fill: colorMotoristas },
    ].filter(d => d.value > 0 && !hiddenSeries.includes(d.name));
  }, [dataChartCustomersList, dataChartVehiclesList, dataCharDriverList, hiddenSeries]);

  const isEmpty = formattedData.length === 0 && hiddenSeries.length === 0;

  const handleLegendClick = (name: string) => {
    if (hiddenSeries.includes(name)) {
      setHiddenSeries(hiddenSeries.filter((k) => k !== name));
    } else {
      setHiddenSeries([...hiddenSeries, name]);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const exportCSV = () => {
    const csvContent = [
      "Categoria,Total Anual",
      ...formattedData.map(d => `${d.name},${d.value}`)
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "pie_chart_export.csv";
    link.click();
    handleMenuClose();
  };

  if (isLoading) {
    return <SkeletonTotalGrowthBarChart />;
  }

  if (isEmpty) {
    return (
      <Grid className="dashboard-chart-surface tw-rounded-xl tw-bg-slate-50 dark:tw-bg-slate-900" container justifyContent="center" alignItems="center" style={{ minHeight: 350, background: "#f9fafb", borderRadius: 12, boxShadow: "none" }}>
        <Typography variant="h5" color="textSecondary" style={typographyPrometo}>
          Nenhum dado disponível.
        </Typography>
      </Grid>
    );
  }

  const renderInteractiveLegend = () => {
    const categories = [
      { name: "Clientes", color: colorClientes },
      { name: "Veículos", color: colorVeiculos },
      { name: "Motoristas", color: colorMotoristas }
    ];
    return (
      <Box display="flex" justifyContent="center" flexWrap="wrap" gap={2} pt={2}>
        {categories.map((cat, index) => {
          const isHidden = hiddenSeries.includes(cat.name);
          return (
            <Box
              key={index}
              onClick={() => handleLegendClick(cat.name)}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', opacity: isHidden ? 0.4 : 1, transition: '0.2s' }}
            >
              <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: isHidden ? '#ccc' : cat.color, mr: 1 }} />
              <Typography variant="body2" sx={{ ...typographyPrometo, color: theme.palette.text.secondary, textDecoration: isHidden ? 'line-through' : 'none' }}>
                {cat.name}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <div className="dashboard-chart-surface tw-relative tw-flex tw-w-full tw-min-w-0 tw-flex-col tw-overflow-hidden tw-rounded-xl tw-bg-slate-50 dark:tw-bg-slate-900" style={{ position: "relative", height: "clamp(350px, 40vw, 440px)", width: "100%", background: "#f9fafb", borderRadius: 12, outline: "none" }} ref={chartRef}>
      <style>{`.dashboard-chart-surface .recharts-wrapper, .dashboard-chart-surface .recharts-surface { outline: none !important; }`}</style>

      <Box display="flex" justifyContent="space-between" alignItems="center" px={2} pt={2}>
        <Typography variant="h6" style={{ ...typographyPrometo, fontWeight: 600, color: theme.palette.success.main || "#00b700" }}>
          Volumes Totais
        </Typography>
        <Box>
          <IconButton className="!tw-bg-white/80 !tw-text-gtr-teal tw-shadow-sm hover:!tw-bg-white dark:!tw-bg-slate-800 dark:!tw-text-slate-100 dark:hover:!tw-bg-slate-700" onClick={handleMenuClick} size="small">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
            </svg>
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <MenuItem onClick={exportCSV} style={typographyPrometo}>Download CSV</MenuItem>
          </Menu>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Tooltip content={<CustomTooltip theme={theme} />} />
          <Pie
            data={formattedData}
            cx="50%"
            cy="50%"
            innerRadius="44%"
            outerRadius="68%"
            paddingAngle={5}
            dataKey="value"
            label={({ percent }) => `${((percent || 0) * 100).toFixed(0)}%`}
            labelLine={false}
            style={{ ...typographyPrometo, fontSize: 11, fontWeight: "bold" }}
          >
            {formattedData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      </Box>
      <Box sx={{ width: "100%", maxHeight: 76, overflowY: 'auto', pb: 2 }}>
        {renderInteractiveLegend()}
      </Box>
    </div>
  );
};

export default TotalGrowthPieChart;
